import React, { useState, useEffect, useRef } from 'react';
import { FaComments, FaTimes, FaPaperPlane } from 'react-icons/fa';

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      text: 'Hello! 👋 Welcome to RENGIE Solar. How can I help you today?',
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const quickReplies = [
    { text: '💰 Calculate Savings', action: 'calculator' },
    { text: '📞 Request Callback', action: 'callback' },
    { text: '💵 Check Subsidy', action: 'subsidy' },
    { text: '📍 Service Areas', action: 'areas' }
  ];

  // Smart Keyword Detection Function
  const getSmartResponse = (userMessage) => {
    const msg = userMessage.toLowerCase();
    
    // Price/Cost Related
    if (msg.includes('price') || msg.includes('cost') || msg.includes('kitna') || msg.includes('paisa') || msg.includes('rate')) {
      return 'Solar system pricing:\n\n• 1kW: ₹65,000-70,000\n• 2kW: ₹1,30,000-1,40,000\n• 3kW: ₹1,95,000-2,10,000\n• 5kW: ₹3,25,000-3,50,000\n\nPM Suryaghar subsidy available up to ₹78,000!\n\n📞 Call for exact quote: +91-9031654981';
    }
    
    // Subsidy Related
    if (msg.includes('subsidy') || msg.includes('yojana') || msg.includes('government') || msg.includes('scheme')) {
      return 'PM Suryaghar Muft Bijli Yojana:\n\n• 1kW: ₹30,000 subsidy\n• 2kW: ₹60,000 subsidy\n• 3kW: ₹78,000 subsidy\n• 5kW: ₹78,000 subsidy\n\nRENGIE handles complete subsidy paperwork for you! 📄✅';
    }
    
    // Installation Time
    if (msg.includes('time') || msg.includes('kitne din') || msg.includes('installation') || msg.includes('lagega')) {
      return 'Installation Timeline:\n\n• Physical installation: 2-4 days\n• Net-metering approval: 30-60 days\n• Complete process: 45-75 days\n\nRENGIE being empanelled vendor expedites approvals! ⚡';
    }
    
    // Savings/Bill Reduction
    if (msg.includes('saving') || msg.includes('bill') || msg.includes('bachega') || msg.includes('reduce')) {
      return 'Electricity bill savings:\n\n• Residential: 85-95% reduction\n• Commercial: 75-85% reduction\n\nExample: ₹3,000 bill → ₹300\n\nUse our calculator to estimate your exact savings! 💰';
    }
    
    // Area/Location
    if (msg.includes('area') || msg.includes('location') || msg.includes('patna') || msg.includes('ranchi') || msg.includes('bihar') || msg.includes('jharkhand')) {
      return 'Our Service Areas:\n\n📍 Bihar:\nPatna, Gaya, Chhapra, Siwan\n\n📍 Jharkhand:\nRanchi, Hazaribagh, Ramgarh, Dhanbad, Jamshedpur\n\nOffice:\n• Patna: Road 15E, Rajeev Nagar\n• Ranchi: Tigga Complex, Harmu';
    }
    
    // Warranty/Maintenance
    if (msg.includes('warranty') || msg.includes('maintenance') || msg.includes('service') || msg.includes('guarantee')) {
      return 'Warranty & Maintenance:\n\n✅ Solar panels: 25-30 years\n✅ Inverter: 10-15 years\n✅ Free O&M: 5 years included\n✅ Online monitoring platform\n\nLifetime support from RENGIE! 🛠️';
    }
    
    // Roof/Space
    if (msg.includes('roof') || msg.includes('space') || msg.includes('chhat') || msg.includes('area required')) {
      return 'Roof Space Required:\n\n• 1kW = 100 sq.ft\n• 3kW = 300 sq.ft\n• 5kW = 500 sq.ft\n\nRequirements:\n✓ Shadow-free area\n✓ South/Southeast facing\n✓ RCC or strong tin roof\n\nFree site survey available! 🏠';
    }
    
    // Contact/Call
    if (msg.includes('contact') || msg.includes('call') || msg.includes('number') || msg.includes('phone')) {
      return 'Contact RENGIE:\n\n📞 Phone:\n+91-9031654981\n+91-9031654984\n+91-9031654985\n\n📧 Email:\naskus@rengie.in\n\n🌐 Website:\nwww.rengie.in\n\nWorking: Mon-Sat, 9 AM - 6 PM';
    }
    
    // EMI/Finance
    if (msg.includes('emi') || msg.includes('loan') || msg.includes('finance') || msg.includes('installment')) {
      return '💳 Financing Options:\n\n• 100% financing available\n• EMI starting ₹1,500/month\n• Zero down payment\n• Quick approval\n\nPre-approved for govt employees!\n\n📞 Call for loan details: 9031654981';
    }
    
    // Working/Performance
    if (msg.includes('work') || msg.includes('rain') || msg.includes('cloudy') || msg.includes('barish') || msg.includes('monsoon')) {
      return 'Solar Performance:\n\n☀️ Sunny days: 100% output\n⛅ Cloudy days: 10-25% output\n🌧️ Rainy days: Still generates power\n\nAnnual generation remains predictable! Modern panels work in diffused light too.';
    }
    
    // Net Metering
    if (msg.includes('net meter') || msg.includes('grid') || msg.includes('electricity board') || msg.includes('discom')) {
      return 'Net Metering:\n\n✅ Bi-directional meter installed\n✅ Export excess power to grid\n✅ Get credit at same rate\n✅ Monthly net adjustment\n\nRENGIE handles SBPDCL/NBPDCL/JBVNL approvals! ⚡';
    }
    
    // Battery/Backup
    if (msg.includes('battery') || msg.includes('backup') || msg.includes('power cut') || msg.includes('light chala gaya')) {
      return 'Backup Options:\n\n🔋 On-Grid System (Standard):\n• No backup during power cuts\n• Lower cost\n• Best for areas with stable grid\n\n🔋 Hybrid System (With Battery):\n• Backup during power cuts\n• Higher initial cost\n• Recommended for frequent outages\n\nCall to discuss: 9031654981';
    }
    
    // Thank you / Appreciation
    if (msg.includes('thank') || msg.includes('thanks') || msg.includes('dhanyavaad') || msg.includes('shukriya')) {
      return 'You\'re welcome! 😊\n\nNeed anything else?\n\n📞 Call: +91-9031654981\n💬 WhatsApp: Click green button below\n\nHappy to help! 🌞';
    }
    
    // Hello/Hi
    if (msg.includes('hello') || msg.includes('hi') || msg.includes('hey') || msg.includes('namaste')) {
      return 'Hello! 👋 Welcome to RENGIE Solar!\n\nI can help you with:\n• Solar pricing & savings\n• PM Suryaghar subsidy\n• Installation process\n• Service areas\n\nWhat would you like to know?';
    }
    
    // Default Response
    return 'Thanks for your message!\n\nFor detailed assistance:\n\n📞 Call us: +91-9031654981\n💬 WhatsApp: Click green button\n✉️ Email: askus@rengie.in\n\nOr use quick options below for instant answers! 😊';
  };

  const handleQuickReply = (action) => {
    let botResponse = '';
    
    switch(action) {
      case 'calculator':
        botResponse = 'Great! Please visit our Solar Calculator section to estimate your savings. Scroll up or click the menu to find the calculator.';
        break;
      case 'callback':
        botResponse = 'Sure! Please call us at:\n📞 +91-9031654981\n📞 +91-9031654984\n\nOr fill the contact form. Our engineer will call you within 30 minutes during working hours (9 AM - 6 PM).';
        break;
      case 'subsidy':
        botResponse = 'Under PM Suryaghar Yojana:\n\n• 1kW: ₹30,000\n• 2kW: ₹60,000\n• 3kW: ₹78,000\n• 5kW+: ₹78,000\n\nWe handle all subsidy paperwork! 📄';
        break;
      case 'areas':
        botResponse = 'We serve across:\n\n📍 Bihar:\nPatna, Gaya, Chhapra, Siwan\n\n📍 Jharkhand:\nRanchi, Hazaribagh, Ramgarh, Dhanbad, Jamshedpur';
        break;
      default:
        botResponse = 'How else can I help you?';
    }

    const userMsg = { 
      type: 'user', 
      text: quickReplies.find(q => q.action === action).text, 
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) 
    };
    
    const botMsg = { 
      type: 'bot', 
      text: botResponse, 
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) 
    };

    setMessages(prev => [...prev, userMsg, botMsg]);
  };

  const handleSendMessage = () => {
    if (inputMessage.trim()) {
      const userMsg = { 
        type: 'user', 
        text: inputMessage, 
        time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) 
      };
      
      // Get smart response based on user input
      const smartResponse = getSmartResponse(inputMessage);
      
      const botMsg = { 
        type: 'bot', 
        text: smartResponse, 
        time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) 
      };

      setMessages(prev => [...prev, userMsg, botMsg]);
      setInputMessage('');
    }
  };

  return (
    <>
      {/* Chat Button */}
      <div 
        className="chatbot-button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: 'fixed',
          bottom: isOpen ? '420px' : '100px',
          right: '30px',
          width: '60px',
          height: '60px',
          background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 8px 25px rgba(33, 150, 243, 0.4)',
          zIndex: 998,
          transition: 'all 0.3s ease',
          animation: isOpen ? 'none' : 'pulse-chat 2s infinite'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.boxShadow = '0 12px 35px rgba(33, 150, 243, 0.6)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 8px 25px rgba(33, 150, 243, 0.4)';
        }}
      >
        {isOpen ? (
          <FaTimes style={{ fontSize: '1.8rem', color: '#ffffff' }} />
        ) : (
          <FaComments style={{ fontSize: '1.8rem', color: '#ffffff' }} />
        )}
      </div>

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="chatbot-window"
          style={{
            position: 'fixed',
            bottom: '100px',
            right: '30px',
            width: '350px',
            height: '500px',
            background: '#ffffff',
            borderRadius: '20px',
            boxShadow: '0 15px 50px rgba(0, 0, 0, 0.2)',
            zIndex: 997,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            animation: 'slideUp 0.3s ease'
          }}
        >
          {/* Chat Header */}
          <div 
            className="chat-header"
            style={{
              background: 'linear-gradient(135deg, #1B3F6E 0%, #2E5C8A 100%)',
              padding: '20px',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              gap: '15px'
            }}
          >
            <div 
              style={{
                width: '50px',
                height: '50px',
                background: '#00D084',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                fontWeight: 'bold'
              }}
            >
              R
            </div>
            <div style={{ flex: 1 }}>
              <h6 className="mb-0 fw-bold">RENGIE Solar </h6>
              <small style={{ opacity: 0.9 }}>
                <span style={{ 
                  display: 'inline-block', 
                  width: '8px', 
                  height: '8px', 
                  background: '#4CAF50', 
                  borderRadius: '50%',
                  marginRight: '5px'
                }}></span>
                Online - Ask me anything!
              </small>
            </div>
          </div>

          {/* Chat Messages */}
          <div 
            ref={messagesContainerRef}
            className="chat-messages"
            style={{
              flex: 1,
              padding: '20px',
              overflowY: 'auto',
              background: '#F8F9FA',
              scrollBehavior: 'smooth'
            }}
          >
            {messages.map((msg, index) => (
              <div 
                key={index}
                style={{
                  marginBottom: '15px',
                  display: 'flex',
                  justifyContent: msg.type === 'bot' ? 'flex-start' : 'flex-end',
                  animation: 'messageSlide 0.3s ease'
                }}
              >
                <div 
                  style={{
                    maxWidth: '75%',
                    padding: '12px 16px',
                    borderRadius: msg.type === 'bot' ? '10px 10px 10px 0' : '10px 10px 0 10px',
                    background: msg.type === 'bot' ? '#ffffff' : 'linear-gradient(135deg, #00D084 0%, #00A86B 100%)',
                    color: msg.type === 'bot' ? '#333' : '#ffffff',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    fontSize: '0.9rem',
                    lineHeight: '1.5',
                    whiteSpace: 'pre-line'
                  }}
                >
                  {msg.text}
                  <div 
                    style={{
                      fontSize: '0.7rem',
                      marginTop: '5px',
                      opacity: 0.7,
                      textAlign: 'right'
                    }}
                  >
                    {msg.time}
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Replies */}
          <div 
            className="quick-replies"
            style={{
              padding: '10px 15px',
              background: '#ffffff',
              borderTop: '1px solid #E0E0E0',
              display: 'flex',
              gap: '8px',
              flexWrap: 'wrap',
              overflowX: 'auto'
            }}
          >
            {quickReplies.map((reply, index) => (
              <button
                key={index}
                onClick={() => handleQuickReply(reply.action)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #00D084',
                  background: '#ffffff',
                  color: '#00D084',
                  borderRadius: '20px',
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#00D084';
                  e.currentTarget.style.color = '#ffffff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#ffffff';
                  e.currentTarget.style.color = '#00D084';
                }}
              >
                {reply.text}
              </button>
            ))}
          </div>

          {/* Chat Input */}
          <div 
            className="chat-input"
            style={{
              padding: '15px',
              background: '#ffffff',
              borderTop: '1px solid #E0E0E0',
              display: 'flex',
              gap: '10px'
            }}
          >
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder="Ask: price, subsidy, time..."
              style={{
                flex: 1,
                padding: '10px 15px',
                border: '1px solid #E0E0E0',
                borderRadius: '25px',
                outline: 'none',
                fontSize: '0.9rem'
              }}
            />
            <button
              onClick={handleSendMessage}
              style={{
                width: '45px',
                height: '45px',
                background: 'linear-gradient(135deg, #00D084 0%, #00A86B 100%)',
                border: 'none',
                borderRadius: '50%',
                color: '#ffffff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <FaPaperPlane />
            </button>
          </div>
        </div>
      )}

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes pulse-chat {
          0%, 100% {
            box-shadow: 0 8px 25px rgba(33, 150, 243, 0.4);
          }
          50% {
            box-shadow: 0 8px 35px rgba(33, 150, 243, 0.7);
          }
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes messageSlide {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .chat-messages::-webkit-scrollbar {
          width: 6px;
        }

        .chat-messages::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }

        .chat-messages::-webkit-scrollbar-thumb {
          background: #00D084;
          border-radius: 10px;
        }

        .chat-messages::-webkit-scrollbar-thumb:hover {
          background: #00A86B;
        }

        @media (max-width: 480px) {
          .chatbot-window {
            width: calc(100vw - 40px) !important;
            right: 20px !important;
            bottom: 80px !important;
          }

          .chatbot-button {
            bottom: ${isOpen ? '600px' : '80px'} !important;
            right: 20px !important;
          }
        }
      `}</style>
    </>
  );
};

export default ChatBot;
